import { onScopeDispose, ref, type Ref } from 'vue';

/**
 * Reactive `navigator.onLine` with `online`/`offline` event subscriptions.
 *
 * `onLine` is unreliable on its own (browsers report true even on a captive
 * portal), but combined with failed fetches it's the best signal we have for
 * "show an offline banner now."
 *
 * Listeners are cleaned up automatically on the calling effect scope.
 */
export function useOnlineStatus(): { isOnline: Ref<boolean> } {
  const isOnline = ref(typeof navigator === 'undefined' ? true : navigator.onLine);

  if (typeof window === 'undefined') {
    return { isOnline };
  }

  const onOnline = (): void => {
    isOnline.value = true;
  };
  const onOffline = (): void => {
    isOnline.value = false;
  };
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);

  onScopeDispose(() => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
  });

  return { isOnline };
}
